import useStore from "@/store"
import { api } from "./base"
import { HTTPError } from "ky"
import { ProcessSession, ShiftStatus } from "@/store/session"
import { createUuid, EnumsOf } from "@/lib/utils"

export const ProcessType = {
  PACKAGING: "PACKAGING",
  IPQC: "IPQC",
  NONE: "NONE",
} as const
export type ProcessType = EnumsOf<typeof ProcessType>

export interface StartShiftPayload {
  shiftId: string
}

export interface StartShiftRes {
  shiftSessionId: string
  shiftSessionStartedAt: string
  shiftSessionStatus: ShiftStatus
}

function clearShiftScopedState() {
  const store = useStore.getState()

  store.ipqc.clearShift()
  store.boilout.clearShift()
  store.packaging.clearShift()
  store.packagingV2.clearShift()
  store.rmpm.clearProcess()
  store.session.endShift()
}

function clearServerShiftStatePreservingOfflineDrafts() {
  const store = useStore.getState()

  if (!store.ipqc.offline.activeSession) {
    store.ipqc.clearShift()
  }

  if (!store.boilout.offline.activeSession) {
    store.boilout.clearShift()
  }

  store.packaging.clearShift()
  store.packagingV2.clearShift()
  store.rmpm.clearProcess()
  store.session.endShift()
}

export async function startShift(payload: StartShiftPayload) {
  const res = await api
    .post<StartShiftRes>("api/shifts/start", {
      json: payload,
    })
    .json()

  const store = useStore.getState()
  store.session.startShift(res.shiftSessionId)
}

export async function discardShift() {
  try {
    await api.get("api/shifts/discard")
    clearShiftScopedState()
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        clearShiftScopedState()
      }
    }

    throw err
  }
}

export async function endShift() {
  try {
    await api.get("api/shifts/end")
    clearShiftScopedState()
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        clearShiftScopedState()
      }
    }

    throw err
  }
}

export interface GetShiftStatusRes {
  shiftSession: ShiftSession | null
}

export interface ShiftSession {
  id: string
  status: ShiftStatus
  startedAt: string
  endedAt: string | null
  shiftName: string
  shiftId: string
  shiftStartedAt: string
  shiftEndedAt: string
  employeeSessions?: ProcessSession[]
}

export async function getShiftStatus() {
  return api.get<GetShiftStatusRes>("api/shifts/status").json()
}

export async function addProcesses(processIds: string[]) {
  const store = useStore.getState()

  for (const processId of processIds) {
    if (
      store.session.session.processes.find(
        (process) => process.processId === processId
      )
    ) {
      continue
    }

    const process = store.user.processes.find(
      (item) => item.id === processId
    )?.name
    if (!process) {
      return
    }

    store.session.addProcess({
      id: createUuid(),
      processName: process,
      status: ShiftStatus.Inactive,
      startedAt: "",
      endedAt: "",
      updatedAt: "",
      processId: processId,
    })
  }
}

export function setSession(session: ShiftSession | null) {
  const store = useStore.getState()

  if (!session) {
    clearServerShiftStatePreservingOfflineDrafts()

    return
  }

  const existingSession = store.session;

  store.session.setSession({
    id: session.id,
    status: session.status,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    processes: session.employeeSessions
      ? session.employeeSessions.map((proc) => {
        // Find existing process (if any)
        const existingProcess = existingSession?.session?.processes?.find(
          (p) => p.processId === proc.processId
        );

        // Get timestamps for comparison
        const existingUpdatedAt = existingProcess?.updatedAt
          ? new Date(existingProcess.updatedAt).getTime()
          : 0;

        // Use updatedAt if exists, else fallback to endedAt or startedAt
        const newUpdatedAt = proc.updatedAt
          ? new Date(proc.updatedAt).getTime()
          : proc.endedAt
            ? new Date(proc.endedAt).getTime()
            : new Date(proc.startedAt || "").getTime();

        // Choose the later updatedAt
        const finalUpdatedAt =
          newUpdatedAt > existingUpdatedAt
            ? proc.updatedAt || proc.endedAt || proc.startedAt
            : existingProcess?.updatedAt || proc.updatedAt || proc.endedAt || proc.startedAt;

        return {
          ...proc,
          updatedAt: finalUpdatedAt,
          endedAt: proc.endedAt || proc.startedAt,
        };
      })
      : [],
  })

  if (session.employeeSessions) {
    for (const process of session.employeeSessions) {
      if (process.processName === "Packaging") {
        store.packaging.startShift({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (process.processName === "Packaging V2") {
        store.packagingV2.startShift({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (process.processName === "In-Process QC") {
        store.ipqc.setShiftDetails({
          id: session.shiftId,
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (
        process.processName === "Raw Material and Packaging Material"
      ) {
        store.rmpm.setProcessDetails({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      }
    }
  }
}

/**
 * Merge session data from server with local state
 * This preserves locally added processes while updating server-provided fields
 * Use this instead of setSession when you want to sync status without losing local changes
 */
export function mergeSession(session: ShiftSession | null) {
  const store = useStore.getState()

  if (!session) {
    clearServerShiftStatePreservingOfflineDrafts()

    return
  }

  store.session.mergeSession({
    id: session.id,
    status: session.status,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    processes: session.employeeSessions
      ? session.employeeSessions.map((proc) => ({
          ...proc,
          updatedAt: proc.updatedAt || proc.endedAt || proc.startedAt,
          endedAt: proc.endedAt || null,
        }))
      : [],
  })

  // Update process-specific stores if needed
  if (session.employeeSessions) {
    for (const process of session.employeeSessions) {
      if (process.processName === "Packaging") {
        store.packaging.startShift({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (process.processName === "Packaging V2") {
        store.packagingV2.startShift({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (process.processName === "In-Process QC") {
        store.ipqc.setShiftDetails({
          id: session.shiftId,
          processId: process.processId,
          shiftId: session.shiftId,
        })
      } else if (
        process.processName === "Raw Material and Packaging Material"
      ) {
        store.rmpm.setProcessDetails({
          processId: process.processId,
          shiftId: session.shiftId,
        })
      }
    }
  }
}

// ---- Shift Data (moved from auth.ts) ----

import { LoginAPIResponse } from "./auth"

export interface GetShiftsRes extends Omit<LoginAPIResponse, "token"> { }

export async function getShifts() {
  const res = await api.get<GetShiftsRes>("api/shifts")

  return res.json()
}
